import { useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

/* Support chat (Chatwoot), answered by Desk/"Aidan", Norman's support agent,
 * from docs/support/knowledge.md (Norman's "Support system — agent-first
 * plan", October 2026; live from 2026-10-06 at John's request).
 *
 * Two Chatwoot inboxes, so dev and live conversations stay apart, picked by
 * the build MODE: 'development' under `vite` (CM's pm2 freightsource-client)
 * and 'production' in `npm run build` (the live app). Not
 * import.meta.env.DEV: CM's .env sets NODE_ENV=production, which makes DEV
 * false even under the dev server.
 *
 * When signed in, the user is passed to Chatwoot with an identifier_hash
 * (GET /support/chat-identity, signed server-side with the matching inbox's
 * CHATWOOT_IDENTITY_SECRET) so Chatwoot can verify it — both inboxes require
 * it. Website tokens are public by design. */
const BASE_URL = 'https://chatwoot.agents.kodie.co.uk';
const WEBSITE_TOKEN = import.meta.env.MODE === 'development'
    ? 'wqDT7jjSzbWnoxc354QiSRNU'   // inbox 1: FreightSource dev (CM)
    : '2fkUHreM6EYMAABhrnBHta1j';  // inbox 3: FreightSource app (live)

const SupportChat = () => {
    const { user } = useAuth();

    useEffect(() => {
        if (window.chatwootSDK || document.getElementById('chatwoot-sdk')) return;
        const script = document.createElement('script');
        script.id = 'chatwoot-sdk';
        script.src = `${BASE_URL}/packs/js/sdk.js`;
        script.defer = true;
        script.async = true;
        script.onload = () => window.chatwootSDK.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL });
        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        // identifier_hash proves to Chatwoot that this browser really is this
        // user (signed server-side, GET /support/chat-identity). Without it
        // anyone could call setUser with someone else's id from the console,
        // and the support agent's account lookup would answer for them.
        let identity = null;
        const apply = () => {
            if (!window.$chatwoot) return;
            if (user?.id) {
                window.$chatwoot.setUser(String(user.id), {
                    name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
                    email: user.email,
                    company_name: user.organization?.name || undefined,
                    ...(identity ? { identifier_hash: identity } : {}),
                });
            } else {
                window.$chatwoot.reset(); // signed out: don't carry the last person over
            }
        };
        let cancelled = false;
        const start = () => {
            if (window.$chatwoot?.hasLoaded) apply();
            window.addEventListener('chatwoot:ready', apply);
        };
        if (user?.id) {
            api.get('support/chat-identity')
                .then((r) => { identity = r?.identifier_hash || null; })
                .catch(() => { identity = null; }) // not configured: plain setUser
                .finally(() => { if (!cancelled) start(); });
        } else {
            start();
        }
        return () => {
            cancelled = true;
            window.removeEventListener('chatwoot:ready', apply);
        };
    }, [user?.id, user?.email, user?.organization?.name]);

    return null;
};

export default SupportChat;
